/** @jsx jsx */

import React, {
  Fragment as _,
  useState,
  useRef,
  useContext,
  useEffect,
  cloneElement,
} from 'react';
import { css as _css } from 'emotion';
import { css, jsx } from '@emotion/core';
import { colors, getButtonFocus } from 'styles/variables';
import setCurrentBlock from 'services/apiHelpers/setCurrentBlock';
import { interactionContext } from './interactionContext';
import checkReturnToInteractionEditing from 'services/checkReturnToInteractionEditing';
import Modal from 'components/UI/Modal';
import getBlockById from 'services/apiHelpers/getBlockById';
import setBlockProps from 'services/apiHelpers/setBlockProps';
import pubSub from 'services/pubSub';
import getStore from 'services/getStore';
import { getHexToRgbWithDefaultFallback } from 'services/hexToRGB';
import { headingStylesForPanelsAndCards } from 'styles/headingStylesForPanelsAndCards';
import { HostingContext } from 'hosting/context';
import { clickRevealEvent } from 'hosting/events';

const store = getStore();
const { lGrey0, lPink } = colors;

const PanelColumn = ({ width, children }) => {
  //
  return (
    <div
      css={css`
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        flex-shrink: 0;
        width: ${width}px;
        margin: 20px;
        cursor: pointer;

        @media (max-width: ${width + 30}px) {
          width: 100% !important;
          max-width: 100% !important;
          height: auto !important;
        }
      `}
    >
      {children}
    </div>
  );
};

const ClickAndRevealSeenTick = ({
  title = '',
  width = 16,
  height = 15,
  color,
}) => {
  const titleComped = `${title ? `${title}. ` : ''}Content has been seen`;

  return (
    <svg
      width="16px"
      height="15px"
      viewBox="0 0 16 15"
      version="1.1"
      aria-describedby="title"
    >
      <title>{'Content has been seen'}</title>
      <g
        id="Page-1"
        stroke="none"
        strokeWidth="1"
        fill="none"
        fillRule="evenodd"
      >
        <polyline
          id="Path"
          stroke={color}
          strokeWidth="2"
          transform="translate(7.486953, 6.527846) rotate(6.000000) translate(-7.486953, -6.527846) "
          points="0.886953048 9.1308343 6.78684502 13.1278461 14.086953 -0.0721539421"
        />
      </g>
    </svg>
  );
};

const ClickAndReveal = ({ children, ...props }) => {
  //

  const [showFeedback, setShowFeedback] = useState(false);

  let [imageText, text, content] = children;

  let imageDescription = '';

  switch (imageText.props.child) {
    case 0: {
      // text
      const doc = new DOMParser().parseFromString(
        imageText.props.children[0].props.content,
        'text/html'
      );
      imageDescription = doc && doc.body.innerText;
      break;
    }

    case 1: {
      // image
      imageDescription = imageText.props.children[1].props.description;
      break;
    }

    case 5: {
      // pdf
      imageDescription = imageText.props.children[5].props.description;
      break;
    }
  }

  // if there's no description added to a link
  // attempt to fall back to the alt text of the image ( if it exists )
  let altTextForAnchorTag = '';

  if (imageText) {
    if (imageText?.props?.child === 1) {
      altTextForAnchorTag = imageText.props.children[1]?.props?.description;
    }

    imageText = cloneElement(imageText, {
      onMediaChange(child) {
        mediaTypeUpdated(child);
      },
    });
  }

  let initialMediaType = imageText?.props?.child ?? 1;
  const [mediaType, mediaTypeUpdated] = useState<number>(initialMediaType);
  //

  const [panelContainsImageZoom, setPanelContainsImageZoom] = useState(false);
  //
  let imgZoomIsActiveElement = false;

  const context: any = useContext(interactionContext);
  const hostingCtx = useContext(HostingContext);

  const { id, updateClickAndRevealSeen } = context;

  const interactionSId = context.props.sId || id;

  const {
    sId,
    includePanelBackground = true,
    panelBackgroundOpacity = 100,
    backgroundColor = '#ffffff',
    //'#0199ff', ^ < that blue
    clickAndRevealPanelTextColor,
    includeLabel = true,
    clickAndRevealLabelTextColor,
    showSeenState = true,
    clickAndRevealShowSeenStateBackgroundColor,
    clickAndRevealShowSeenStateTickColor,
    width,
    height,
    panelOutline,
    panelOutlineColor,
    borderRadius,
    borderColor,
    focusColor,
    layout,
    useGlobalNavigation = false,
    globalNavigation = {},
    includeShadow,
  } = context.props;

  //

  const seen = props?.seen;

  const panelImg = useRef<HTMLDivElement>(null);

  let labelContent;

  if (includeLabel && text?.props?.content) {
    const doc = new DOMParser().parseFromString(
      text.props.content,
      'text/html'
    );
    labelContent = doc && doc.body.innerText;
  }

  let ariaLabel = `${imageDescription ? imageDescription : ''}${
    labelContent ? ` ${labelContent}` : ''
  }${!!showSeenState && seen ? ' Seen.' : ' Not seen.'}`.trim();

  const onClose = () => {
    window.dev() && checkReturnToInteractionEditing(true);
    setShowFeedback(!showFeedback);
  };

  const linkData = {
    url: props?.linkData?.url || `#${props?.navigation}` || '',
    title: props?.linkData?.title || '',
    target: props?.linkData?.target || '_self',
  };

  useEffect(() => {
    if (!useGlobalNavigation) {
      return;
    }

    const panelId = props.id;

    // if this panel has props.navigation we're good and can bail
    if (props.navigation) {
      return;
    }

    // if globalNavigation[props.id] we update the panel props with that location
    // maybe delete the globalNavigation prop from the parent since it'd no longer be needed

    if (globalNavigation[panelId]) {
      const panelBlock = getBlockById(panelId);

      setBlockProps(panelBlock, {
        navigation: globalNavigation[panelId],
      });
    }

    // added empty array dependency
  }, []);

  const { r, g, b } = getHexToRgbWithDefaultFallback(
    backgroundColor,
    '#ffffff'
  );

  let a = panelBackgroundOpacity / 100;
  if (!backgroundColor) {
    a = 0;
  }

  useEffect(() => {
    const pS = pubSub.subscribe(
      `CLICK_AND_REVEAL_FEEDBACK_${props.parentId}`,
      $ => {
        if ($.id !== props.id) {
          if ($.eventType === 'click') {
            setShowFeedback(false);
          }
          return;
        }

        switch ($.eventType) {
          case 'mouseOver': {
            panelImg.current!.style.boxShadow = `0 0 0 2px ${lPink}`;
            return;
          }
          case 'mouseOut': {
            panelImg.current!.style.boxShadow = 'null';
            return;
          }
          case 'click': {
            setShowFeedback(true);
            return;
          }
        }
      }
    );

    return () => pS();
  }, []);

  const returnToLastFocused = () => {
    // get the element we just had focus on
    const findLastFocused = setInterval(() => {
      // TODO - update this doofer to focus back on <a> or button

      const lastFocused: any = document.querySelector(`#panel-${props.id}`);

      if (lastFocused) {
        clearInterval(findLastFocused);
        lastFocused.focus();
      }
    }, 125);
  };

  // fixing this up
  // was the below
  // height: ${!!includeLabel ? '100%' : `${height}px`};
  // changed to : height: ${!!!includeLabel ? `${height}px` : 'auto'};
  // but. this is easier to read
  let useFixedHeightOrAuto = 'auto';

  if (includeLabel === 0) {
    useFixedHeightOrAuto = `${height}px`;
  }

  const panel = (
    <div>
      <div
        ref={panelImg}
        id={`panel-img-${props.id}`}
        className="clickAndRevealPanel"
        css={css`
          position: ${includeLabel ? 'absolute' : 'relative'};
          display: flex;
          justify-content: center;
          align-items: center;
          left: 0;
          top: 0;
          width: ${width}px;
          height: ${height}px;

          .chameleon-image > div,
          .chameleon-image img:not(.zoom-image img) {
            width: ${width}px !important;
            height: ${height}px !important;
          }

          ${includeShadow && 'box-shadow: 0 8px 24px 5px rgba(0, 0, 0, 0.2)'};

          .zoom-image {
            bottom: 5px;
            right: ${5 + borderRadius / 2}px;
            transform: scale(0.8);
          }

          ${mediaType === 0 &&
            `
            padding: ${window.dev() ? '10px' : '20px'};
          `}

          white-space: pre-wrap;
          overflow-wrap: break-word;


          background: ${
            includePanelBackground
              ? `rgba(${r}, ${g}, ${b}, ${a})` || backgroundColor
              : 'transparent'
          };

          color: ${clickAndRevealPanelTextColor};

          ${
            panelOutline
              ? `
                border: 1px solid ${panelOutlineColor || backgroundColor};
                border-radius: ${borderRadius}px;
                `
              : `border-radius: ${borderRadius}px;`
          }

          overflow: hidden;

          * {
            p,
            li {
              line-height: 1.2em !important;
            }
          }

          ${headingStylesForPanelsAndCards}

          cursor: pointer;
          transition: opacity 0.2s;

          ${!window.dev() &&
            `
          &:hover {
            opacity: 0.8;
          }
          `}

          &:focus {
            // ${getButtonFocus(backgroundColor || lGrey0)}
          }

          * div {
            color: ${clickAndRevealPanelTextColor};
          }

          @media (max-width: ${width + 30}px) {
            position: relative !important;
            width: 100% !important;
            height: auto !important;

            .chameleon-image > div,
            .chameleon-image img:not(.zoom-image img) {
              width: calc(100vw - 30px) !important;
              height: auto !important;
            }
          }
        `}
        onKeyUp={
          window.dev()
            ? () => {}
            : e => {
                if (![' ', 'Enter'].includes(e.key) || imgZoomIsActiveElement) {
                  return;
                }

                setShowFeedback(!showFeedback);
              }
        }
      >
        {imageText}
      </div>

      {!!showSeenState && (window.dev() || seen) && (
        <div
          css={css`
            position: absolute;
            display: flex;
            justify-content: center;
            align-items: center;
            right: -22px;
            top: -22px;
            width: 44px;
            height: 44px;
            background: ${clickAndRevealShowSeenStateBackgroundColor ||
              '#1A1A2B'};
            border-radius: 50%;
            pointer-events: none;
          `}
        >
          <ClickAndRevealSeenTick
            title={linkData.title || altTextForAnchorTag || ''}
            color={clickAndRevealShowSeenStateTickColor || '#fff'}
          />
        </div>
      )}
    </div>
  );

  const panelLabel = !!includeLabel ? (
    <div
      id={`panel-label-${props.id}`}
      css={css`
        display: flex;
        justify-content: center;
        color: ${clickAndRevealLabelTextColor};
        width: 100%;
        margin: ${window.dev()
            ? height + (includeShadow ? 5 : 0)
            : height + (includeShadow ? 20 : 15)}px
          0 0 0;
        cursor: auto;
        //double up needed for editing. . .
        * div {
          color: ${clickAndRevealLabelTextColor};
        }

        @media (max-width: ${width + 30}px) {
          margin: 15px 0 0 0 !important;
        }
      `}
    >
      {text}
    </div>
  ) : null;

  return (
    <_>
      <PanelColumn width={width}>
        {!useGlobalNavigation ? (
          <button
            id={`panel-${props.id}`}
            css={css({
              outline: 'none',
              border: 'none',
              margin: 0,
              padding: 0,
              background: 'none',

              '&:focus-visible': {
                [`#panel-img-${props.id}`]: {
                  transition: 'box-shadow 0.2s',
                  boxShadow: (getButtonFocus(
                    focusColor || backgroundColor || lGrey0
                  ) as string)
                    .replace('box-shadow:', '')
                    .trim(),
                },
              },
            })}
            onKeyUp={e => {
              // this prevents the click from firing when typing
              // in the a text field
              if (window.dev()) {
                e.preventDefault();
                return;
              }

              const { activeElement } = document;

              imgZoomIsActiveElement = [
                'expand image',
                'close expanded image view',
              ].includes(String(activeElement?.ariaLabel));
            }}
            //
            onClick={e => {
              if (imgZoomIsActiveElement) {
                return;
              }

              setShowFeedback(!showFeedback);

              const ctr_click = {
                ctr_id: id,
              };

              hostingCtx?.data(clickRevealEvent(context.id, ctr_click));
            }}
            aria-label={ariaLabel}
            //
          >
            {panel}
          </button>
        ) : (
          <a
            id={`panel-${props.id}`}
            href={window.dev() ? null : linkData.url}
            title={linkData.title || altTextForAnchorTag}
            target={linkData.target}
            css={css({
              textDecoration: 'none !important',

              '&:focus': {
                [`#panel-img-${props.id}`]: {
                  transition: 'box-shadow 0.2s',
                  boxShadow: (getButtonFocus(
                    focusColor || backgroundColor || lGrey0
                  ) as string)
                    .replace('box-shadow:', '')
                    .trim(),
                },
              },
            })}
            onFocus={e => {
              // updateCardContent(e);
            }}
            onKeyUp={e => {
              // this prevents the click from firing when typing
              // in the a text field
              if (window.dev()) {
                e.preventDefault();
                return;
              }

              if (e.keyCode === 32) {
                e.currentTarget.click();
              }
            }}
            onClick={e => {
              // make sure <a> does not nav

              if (window.dev()) {
                e.preventDefault();
              }

              if (window.dev() && useGlobalNavigation) {
                if (store.getState().focusedNodeId !== id) {
                  setCurrentBlock({ id });
                }
                pubSub.publish('SET_CLICK_AND_REVEAL_GLOBAL_NAVIGATION_PANEL', {
                  panelId: props.id,
                });
                return;
              }

              if (!linkData.url) {
                alert('No navigation has been set on this panel');
                return;
              }

              updateClickAndRevealSeen(props.id);
            }}
          >
            {panel}
          </a>
        )}
        {panelLabel}
      </PanelColumn>

      {showFeedback && (
        <Modal
          onClose={() => {
            updateClickAndRevealSeen(props.id);

            returnToLastFocused();
            onClose();
          }}
        >
          {React.cloneElement(content, {
            key: layout,
            layout,
            onClose() {
              updateClickAndRevealSeen(props.id);

              returnToLastFocused();
              onClose();
            },
          })}
        </Modal>
      )}
    </_>
  );
};

export default ClickAndReveal;
